import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Key,
  Server,
  Shield,
  Bell,
  Database,
  Globe,
  Lock,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  User,
  Palette,
  FileText,
  Download,
  Search,
  Link,
  Webhook,
  Clock,
  Filter,
  Eye,
  EyeOff,
  Upload,
  Moon,
  Sun,
  Monitor,
  Type,
  Smartphone,
  CreditCard
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

interface APIConfig {
  name: string;
  key: string;
  enabled: boolean;
  status: 'connected' | 'disconnected' | 'error';
}

interface CrawlerSource {
  id: string;
  name: string;
  enabled: boolean;
  lastCrawl: string;
  count: number;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { settings: appSettings, updateSettings, saveToBackend, isSaving, lastSaved } = useSettings();
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [showApiKey, setShowApiKey] = useState<{[key: string]: boolean}>({});
  
  // Sync saveStatus with context's isSaving
  useEffect(() => {
    if (isSaving) {
      setSaveStatus('saving');
    } else if (lastSaved) {
      setSaveStatus('saved');
      const timer = setTimeout(() => setSaveStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSaving, lastSaved]);

  // 계정 설정
  const [accountSettings, setAccountSettings] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    organization: user?.organization || '',
    department: user?.department || '',
    position: user?.position || '',
    phone: '',  // User 타입에 phone 필드 없음 - 추후 확장 가능
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });

  // API 설정
  const [apiConfigs, setApiConfigs] = useState<APIConfig[]>([
    { name: 'Claude API', key: 'sk-ant-api03-***', enabled: true, status: 'connected' },
    { name: 'Gemini API', key: 'AIza***', enabled: true, status: 'connected' },
    { name: 'Perplexity API', key: 'pplx-***', enabled: true, status: 'connected' },
    { name: 'Ollama (로컬)', key: 'http://localhost:11434', enabled: false, status: 'disconnected' },
  ]);

  // 통합 설정
  const [integrationSettings, setIntegrationSettings] = useState({
    ntisApiKey: '9p445x2uhb9bqin74242',
    ntisEnabled: true,
    googleOAuthEnabled: true,
    googleClientId: '',
    webhookUrl: '',
    webhookEnabled: false,
    webhookEvents: ['new_grant', 'deadline_reminder', 'proposal_complete'],
  });

  // 보안 설정
  const [securitySettings, setSecuritySettings] = useState({
    useExternalAPI: true,
    useLocalLLM: false,
    dataEncryption: true,
    auditLog: true,
    nationalSecurityMode: false,
    sessionTimeout: 30,
    ipWhitelist: '',
  });

  // 알림 설정
  const [notificationSettings, setNotificationSettings] = useState({
    newGrants: true,
    deadlineReminder: true,
    matchingAlerts: true,
    evaluationResults: true,
    emailNotifications: false,
    pushNotifications: true,
    reminderDays: [7, 3, 1],
  });

  // 내보내기 설정
  const [exportSettings, setExportSettings] = useState({
    defaultFormat: 'docx',
    includeTableOfContents: true,
    includeCoverPage: true,
    defaultTemplate: 'standard',
    autoNumbering: true,
    logoPath: '',
    companyName: '',
  });

  // 크롤링 설정
  const [crawlerSettings, setCrawlerSettings] = useState({
    autoRefresh: true,
    refreshInterval: 6, // hours
    keywords: ['AI', '인공지능', 'R&D', '연구개발', '스마트팩토리'],
    excludeKeywords: [],
  });

  const [crawlerSources, setCrawlerSources] = useState<CrawlerSource[]>([
    { id: 'ntis', name: 'NTIS (국가과학기술정보서비스)', enabled: true, lastCrawl: '2026-01-08 21:00', count: 156 },
    { id: 'iris', name: 'IRIS (범부처통합연구지원시스템)', enabled: true, lastCrawl: '2026-01-08 20:30', count: 89 },
    { id: 'bizinfo', name: '기업마당 (bizinfo.go.kr)', enabled: true, lastCrawl: '2026-01-08 19:00', count: 234 },
    { id: 'keit', name: 'KEIT (한국산업기술평가관리원)', enabled: true, lastCrawl: '2026-01-08 18:00', count: 45 },
    { id: 'iitp', name: 'IITP (정보통신기획평가원)', enabled: true, lastCrawl: '2026-01-08 17:00', count: 32 },
    { id: 'nrf', name: 'NRF (한국연구재단)', enabled: false, lastCrawl: '-', count: 0 },
    { id: 'ketep', name: 'KETEP (한국에너지기술평가원)', enabled: false, lastCrawl: '-', count: 0 },
    { id: 'khidi', name: 'KHIDI (한국보건산업진흥원)', enabled: false, lastCrawl: '-', count: 0 },
  ]);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      await saveToBackend();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('idle');
    }
  };

  const toggleApiKeyVisibility = (name: string) => {
    setShowApiKey(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const tabs = [
    { id: 'general', label: '일반', icon: SettingsIcon },
    { id: 'account', label: '계정', icon: User },
    { id: 'api', label: 'API 설정', icon: Key },
    { id: 'integrations', label: '통합', icon: Link },
    { id: 'security', label: '보안', icon: Shield },
    { id: 'notifications', label: '알림', icon: Bell },
    { id: 'export', label: '내보내기', icon: Download },
    { id: 'crawling', label: '크롤링', icon: Search },
    { id: 'database', label: '데이터베이스', icon: Database },
    { id: 'subscription', label: '구독/결제', icon: CreditCard },
  ];

  const renderToggle = (checked: boolean, onChange: (checked: boolean) => void, color: string = 'emerald') => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-${color}-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-${color}-600`}></div>
    </label>
  );

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">설정</h1>
          <p className="text-gray-600 mt-2">시스템 설정 및 개인화 옵션을 관리합니다</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {saveStatus === 'saving' ? (
            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
          ) : saveStatus === 'saved' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <Save className="h-5 w-5 mr-2" />
          )}
          {saveStatus === 'saving' ? '저장 중...' : saveStatus === 'saved' ? '저장됨' : '저장'}
        </button>
      </div>

      <div className="flex gap-6">
        {/* 탭 네비게이션 */}
        <div className="w-64 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* 설정 내용 */}
        <div className="flex-1">
          {/* 일반 설정 */}
          {activeTab === 'general' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">일반 설정</h2>
              <p className="text-gray-600">언어, 테마, 글꼴 크기 등 기본 설정을 관리합니다.</p>
              
              <div className="space-y-6">
                {/* 언어 설정 */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">언어</h3>
                      <p className="text-sm text-gray-500">인터페이스 언어 설정</p>
                    </div>
                  </div>
                  <select
                    value={appSettings.language}
                    onChange={(e) => updateSettings({ language: e.target.value as 'ko' | 'en' | 'ja' | 'zh' })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="ko">한국어</option>
                    <option value="en">English</option>
                    <option value="ja">日本語</option>
                    <option value="zh">中文</option>
                  </select>
                </div>

                {/* 테마 설정 */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Palette className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">테마</h3>
                      <p className="text-sm text-gray-500">화면 테마 선택</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {[
                      { value: 'light', icon: Sun, label: '라이트' },
                      { value: 'dark', icon: Moon, label: '다크' },
                      { value: 'system', icon: Monitor, label: '시스템' },
                    ].map((theme) => {
                      const ThemeIcon = theme.icon;
                      return (
                        <button
                          key={theme.value}
                          onClick={() => updateSettings({ theme: theme.value as 'light' | 'dark' | 'system' })}
                          className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
                            appSettings.theme === theme.value
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <ThemeIcon className="h-4 w-4 mr-2" />
                          {theme.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 글꼴 크기 */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Type className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">글꼴 크기</h3>
                      <p className="text-sm text-gray-500">텍스트 크기 조절</p>
                    </div>
                  </div>
                  <select
                    value={appSettings.fontSize}
                    onChange={(e) => updateSettings({ fontSize: e.target.value as 'small' | 'medium' | 'large' | 'xlarge' })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="small">작게</option>
                    <option value="medium">보통</option>
                    <option value="large">크게</option>
                    <option value="xlarge">매우 크게</option>
                  </select>
                </div>

                {/* 콤팩트 모드 */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Smartphone className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">콤팩트 모드</h3>
                      <p className="text-sm text-gray-500">UI 요소 간격 줄이기</p>
                    </div>
                  </div>
                  {renderToggle(appSettings.compactMode, (v) => updateSettings({ compactMode: v }))}
                </div>

                {/* 자동 저장 */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Save className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">자동 저장</h3>
                      <p className="text-sm text-gray-500">작업 내용 자동 저장</p>
                    </div>
                  </div>
                  {renderToggle(appSettings.autoSave, (v) => updateSettings({ autoSave: v }))}
                </div>
              </div>
            </div>
          )}

          {/* 계정 설정 */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              {/* 프로필 정보 */}
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">프로필 정보</h2>
                <p className="text-gray-600">개인 정보 및 소속을 수정합니다.</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                    <input
                      type="text"
                      value={accountSettings.fullName}
                      onChange={(e) => setAccountSettings({...accountSettings, fullName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                    <input
                      type="email"
                      value={accountSettings.email}
                      onChange={(e) => setAccountSettings({...accountSettings, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">소속 기관</label>
                    <input
                      type="text"
                      value={accountSettings.organization}
                      onChange={(e) => setAccountSettings({...accountSettings, organization: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="예: 국립안동대학교"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">부서</label>
                    <input
                      type="text"
                      value={accountSettings.department}
                      onChange={(e) => setAccountSettings({...accountSettings, department: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="예: 산학협력단"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">직위</label>
                    <input
                      type="text"
                      value={accountSettings.position}
                      onChange={(e) => setAccountSettings({...accountSettings, position: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="예: 연구원"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                    <input
                      type="tel"
                      value={accountSettings.phone}
                      onChange={(e) => setAccountSettings({...accountSettings, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="예: 054-820-5000"
                    />
                  </div>
                </div>
              </div>

              {/* 비밀번호 변경 */}
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">비밀번호 변경</h2>
                <p className="text-gray-600">계정 비밀번호를 변경합니다.</p>
                
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">현재 비밀번호</label>
                    <input
                      type="password"
                      value={accountSettings.currentPassword}
                      onChange={(e) => setAccountSettings({...accountSettings, currentPassword: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
                    <input
                      type="password"
                      value={accountSettings.newPassword}
                      onChange={(e) => setAccountSettings({...accountSettings, newPassword: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호 확인</label>
                    <input
                      type="password"
                      value={accountSettings.confirmPassword}
                      onChange={(e) => setAccountSettings({...accountSettings, confirmPassword: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                    비밀번호 변경
                  </button>
                </div>
              </div>

              {/* 2단계 인증 */}
              <div className="card space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">2단계 인증 (2FA)</h2>
                    <p className="text-gray-600 mt-1">추가 보안을 위한 2단계 인증을 설정합니다.</p>
                  </div>
                  {renderToggle(accountSettings.twoFactorEnabled, (v) => setAccountSettings({...accountSettings, twoFactorEnabled: v}))}
                </div>
                {accountSettings.twoFactorEnabled && (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm text-emerald-700">
                      2단계 인증이 활성화되었습니다. Google Authenticator 또는 호환 앱을 사용하여 QR 코드를 스캔하세요.
                    </p>
                    <div className="mt-4 flex items-center space-x-4">
                      <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500 text-sm">QR 코드</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>수동 입력 코드:</p>
                        <code className="bg-gray-100 px-2 py-1 rounded">XXXX-XXXX-XXXX-XXXX</code>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* API 설정 */}
          {activeTab === 'api' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">API 설정</h2>
              <p className="text-gray-600">외부 AI API 및 로컬 LLM 연결을 설정합니다.</p>
              
              <div className="space-y-4">
                {apiConfigs.map((config, index) => (
                  <div key={config.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${
                        config.status === 'connected' ? 'bg-green-500' :
                        config.status === 'error' ? 'bg-red-500' : 'bg-gray-400'
                      }`} />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{config.name}</h3>
                        <div className="flex items-center space-x-2">
                          <input
                            type={showApiKey[config.name] ? 'text' : 'password'}
                            value={config.key}
                            onChange={(e) => {
                              const newConfigs = [...apiConfigs];
                              newConfigs[index].key = e.target.value;
                              setApiConfigs(newConfigs);
                            }}
                            className="text-sm text-gray-500 bg-transparent border-none focus:ring-0 p-0 w-64"
                          />
                          <button
                            onClick={() => toggleApiKeyVisibility(config.name)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showApiKey[config.name] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`text-sm ${
                        config.status === 'connected' ? 'text-green-600' :
                        config.status === 'error' ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {config.status === 'connected' ? '연결됨' :
                         config.status === 'error' ? '오류' : '연결 안됨'}
                      </span>
                      <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                        테스트
                      </button>
                      {renderToggle(config.enabled, (v) => {
                        const newConfigs = [...apiConfigs];
                        newConfigs[index].enabled = v;
                        setApiConfigs(newConfigs);
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">LLM 사용 모드</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      보안이 필요한 경우 Ollama 로컬 LLM을 활성화하고 외부 API를 비활성화하세요.
                      국정원 보안 지침 준수가 필요한 경우 보안 탭에서 국가보안모드를 활성화하세요.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 통합 설정 */}
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              {/* NTIS API */}
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">NTIS API</h2>
                <p className="text-gray-600">국가과학기술정보서비스(NTIS) API 연동을 설정합니다.</p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">NTIS API 사용</h3>
                      <p className="text-sm text-gray-500">R&D 과제 검색 서비스 연동</p>
                    </div>
                    {renderToggle(integrationSettings.ntisEnabled, (v) => setIntegrationSettings({...integrationSettings, ntisEnabled: v}))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API 키 (통합인증키)</label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={integrationSettings.ntisApiKey}
                        onChange={(e) => setIntegrationSettings({...integrationSettings, ntisApiKey: e.target.value})}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="NTIS 통합인증키 입력"
                      />
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        연결 테스트
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      <a href="https://www.ntis.go.kr/rndopen/openApi/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        NTIS OpenAPI 포털
                      </a>에서 API 키를 발급받으세요.
                    </p>
                  </div>
                </div>
              </div>

              {/* Google OAuth */}
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Google OAuth</h2>
                <p className="text-gray-600">Google 계정 로그인 연동을 설정합니다.</p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">Google 로그인 사용</h3>
                      <p className="text-sm text-gray-500">Google 계정으로 로그인 허용</p>
                    </div>
                    {renderToggle(integrationSettings.googleOAuthEnabled, (v) => setIntegrationSettings({...integrationSettings, googleOAuthEnabled: v}))}
                  </div>
                  {integrationSettings.googleOAuthEnabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Google Client ID</label>
                      <input
                        type="text"
                        value={integrationSettings.googleClientId}
                        onChange={(e) => setIntegrationSettings({...integrationSettings, googleClientId: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="xxxxx.apps.googleusercontent.com"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 웹훅 */}
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">웹훅 (Webhook)</h2>
                <p className="text-gray-600">외부 서비스로 이벤트 알림을 전송합니다.</p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">웹훅 사용</h3>
                      <p className="text-sm text-gray-500">이벤트 발생 시 외부 URL로 알림 전송</p>
                    </div>
                    {renderToggle(integrationSettings.webhookEnabled, (v) => setIntegrationSettings({...integrationSettings, webhookEnabled: v}))}
                  </div>
                  {integrationSettings.webhookEnabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">웹훅 URL</label>
                        <input
                          type="url"
                          value={integrationSettings.webhookUrl}
                          onChange={(e) => setIntegrationSettings({...integrationSettings, webhookUrl: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="https://your-server.com/webhook"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">전송할 이벤트</label>
                        <div className="space-y-2">
                          {[
                            { id: 'new_grant', label: '새 공모사업 발견' },
                            { id: 'deadline_reminder', label: '마감일 알림' },
                            { id: 'proposal_complete', label: '제안서 생성 완료' },
                            { id: 'evaluation_complete', label: '평가 완료' },
                          ].map((event) => (
                            <label key={event.id} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={integrationSettings.webhookEvents.includes(event.id)}
                                onChange={(e) => {
                                  const events = e.target.checked
                                    ? [...integrationSettings.webhookEvents, event.id]
                                    : integrationSettings.webhookEvents.filter(e => e !== event.id);
                                  setIntegrationSettings({...integrationSettings, webhookEvents: events});
                                }}
                                className="rounded text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-sm text-gray-700">{event.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 보안 설정 */}
          {activeTab === 'security' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">보안 설정</h2>
              <p className="text-gray-600">데이터 보안 및 접근 제어를 설정합니다.</p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">외부 API 사용</h3>
                      <p className="text-sm text-gray-500">Claude, Gemini, Perplexity API 사용</p>
                    </div>
                  </div>
                  {renderToggle(securitySettings.useExternalAPI, (v) => setSecuritySettings({...securitySettings, useExternalAPI: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Server className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">로컬 LLM 사용</h3>
                      <p className="text-sm text-gray-500">Ollama 기반 내부 서버 사용</p>
                    </div>
                  </div>
                  {renderToggle(securitySettings.useLocalLLM, (v) => setSecuritySettings({...securitySettings, useLocalLLM: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Lock className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">데이터 암호화</h3>
                      <p className="text-sm text-gray-500">저장 데이터 AES-256 암호화</p>
                    </div>
                  </div>
                  {renderToggle(securitySettings.dataEncryption, (v) => setSecuritySettings({...securitySettings, dataEncryption: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">감사 로그</h3>
                      <p className="text-sm text-gray-500">모든 활동 기록 저장</p>
                    </div>
                  </div>
                  {renderToggle(securitySettings.auditLog, (v) => setSecuritySettings({...securitySettings, auditLog: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">세션 타임아웃</h3>
                      <p className="text-sm text-gray-500">비활성 시 자동 로그아웃</p>
                    </div>
                  </div>
                  <select
                    value={securitySettings.sessionTimeout}
                    onChange={(e) => setSecuritySettings({...securitySettings, sessionTimeout: parseInt(e.target.value)})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value={15}>15분</option>
                    <option value={30}>30분</option>
                    <option value={60}>1시간</option>
                    <option value={120}>2시간</option>
                    <option value={0}>사용 안함</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-red-600" />
                    <div>
                      <h3 className="font-medium text-red-900">국가보안모드</h3>
                      <p className="text-sm text-red-700">국정원 보안 지침 준수 (외부 API 차단)</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={securitySettings.nationalSecurityMode}
                      onChange={(e) => {
                        setSecuritySettings({
                          ...securitySettings, 
                          nationalSecurityMode: e.target.checked,
                          useExternalAPI: e.target.checked ? false : securitySettings.useExternalAPI,
                          useLocalLLM: e.target.checked ? true : securitySettings.useLocalLLM
                        });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* 알림 설정 */}
          {activeTab === 'notifications' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">알림 설정</h2>
              <p className="text-gray-600">알림 수신 방법과 종류를 설정합니다.</p>
              
              <div className="space-y-4">
                {[
                  { key: 'newGrants', label: '새 공모사업 알림', desc: '조건에 맞는 새 공모사업 발견 시' },
                  { key: 'deadlineReminder', label: '마감일 알림', desc: '공모사업 마감 D-7, D-3, D-1 알림' },
                  { key: 'matchingAlerts', label: '매칭 알림', desc: '기업-공모사업 매칭 결과 알림' },
                  { key: 'evaluationResults', label: '평가 결과 알림', desc: '가상 평가 완료 시 알림' },
                  { key: 'pushNotifications', label: '푸시 알림', desc: '브라우저 푸시 알림 수신' },
                  { key: 'emailNotifications', label: '이메일 알림', desc: '중요 알림을 이메일로도 수신' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">{item.label}</h3>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                    {renderToggle(
                      notificationSettings[item.key as keyof typeof notificationSettings] as boolean,
                      (v) => setNotificationSettings({...notificationSettings, [item.key]: v})
                    )}
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">마감일 알림 시점</h3>
                <div className="flex flex-wrap gap-2">
                  {[1, 3, 5, 7, 14, 30].map((day) => (
                    <button
                      key={day}
                      onClick={() => {
                        const days = notificationSettings.reminderDays.includes(day)
                          ? notificationSettings.reminderDays.filter(d => d !== day)
                          : [...notificationSettings.reminderDays, day].sort((a, b) => b - a);
                        setNotificationSettings({...notificationSettings, reminderDays: days});
                      }}
                      className={`px-3 py-1 rounded-full text-sm ${
                        notificationSettings.reminderDays.includes(day)
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      D-{day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 내보내기 설정 */}
          {activeTab === 'export' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">내보내기 설정</h2>
              <p className="text-gray-600">문서 내보내기 기본 옵션을 설정합니다.</p>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">기본 파일 형식</h3>
                    <p className="text-sm text-gray-500">사업계획서 내보내기 기본 형식</p>
                  </div>
                  <select
                    value={exportSettings.defaultFormat}
                    onChange={(e) => setExportSettings({...exportSettings, defaultFormat: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="docx">Microsoft Word (.docx)</option>
                    <option value="pdf">PDF (.pdf)</option>
                    <option value="hwp">한글 (.hwp)</option>
                    <option value="md">Markdown (.md)</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">기본 템플릿</h3>
                    <p className="text-sm text-gray-500">사업계획서 기본 템플릿 스타일</p>
                  </div>
                  <select
                    value={exportSettings.defaultTemplate}
                    onChange={(e) => setExportSettings({...exportSettings, defaultTemplate: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="standard">표준형</option>
                    <option value="ntis">NTIS 표준</option>
                    <option value="startup">스타트업형</option>
                    <option value="academic">학술연구형</option>
                    <option value="custom">사용자 정의</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">목차 포함</h3>
                    <p className="text-sm text-gray-500">문서에 자동 목차 추가</p>
                  </div>
                  {renderToggle(exportSettings.includeTableOfContents, (v) => setExportSettings({...exportSettings, includeTableOfContents: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">표지 포함</h3>
                    <p className="text-sm text-gray-500">문서에 표지 페이지 추가</p>
                  </div>
                  {renderToggle(exportSettings.includeCoverPage, (v) => setExportSettings({...exportSettings, includeCoverPage: v}))}
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">자동 번호 매기기</h3>
                    <p className="text-sm text-gray-500">제목에 자동 번호 추가 (1.1, 1.2, ...)</p>
                  </div>
                  {renderToggle(exportSettings.autoNumbering, (v) => setExportSettings({...exportSettings, autoNumbering: v}))}
                </div>

                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">기업/기관 이름 (표지용)</label>
                    <input
                      type="text"
                      value={exportSettings.companyName}
                      onChange={(e) => setExportSettings({...exportSettings, companyName: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="예: 국립안동대학교 산학협력단"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">로고 이미지</label>
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                        {exportSettings.logoPath ? (
                          <img src={exportSettings.logoPath} alt="로고" className="max-w-full max-h-full" />
                        ) : (
                          <Upload className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                      <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                        이미지 업로드
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 크롤링 설정 */}
          {activeTab === 'crawling' && (
            <div className="space-y-6">
              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">크롤링 설정</h2>
                <p className="text-gray-600">공모사업 수집 주기와 소스를 설정합니다.</p>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">자동 새로고침</h3>
                      <p className="text-sm text-gray-500">공모사업 자동 수집</p>
                    </div>
                    {renderToggle(crawlerSettings.autoRefresh, (v) => setCrawlerSettings({...crawlerSettings, autoRefresh: v}))}
                  </div>

                  {crawlerSettings.autoRefresh && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gray-900">수집 주기</h3>
                        <p className="text-sm text-gray-500">자동 수집 간격</p>
                      </div>
                      <select
                        value={crawlerSettings.refreshInterval}
                        onChange={(e) => setCrawlerSettings({...crawlerSettings, refreshInterval: parseInt(e.target.value)})}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value={1}>1시간마다</option>
                        <option value={3}>3시간마다</option>
                        <option value={6}>6시간마다</option>
                        <option value={12}>12시간마다</option>
                        <option value={24}>매일</option>
                      </select>
                    </div>
                  )}

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-3">관심 키워드</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {crawlerSettings.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm"
                        >
                          {keyword}
                          <button
                            onClick={() => {
                              const newKeywords = crawlerSettings.keywords.filter((_, i) => i !== index);
                              setCrawlerSettings({...crawlerSettings, keywords: newKeywords});
                            }}
                            className="ml-2 text-emerald-500 hover:text-emerald-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="키워드 입력 후 Enter"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.target as HTMLInputElement;
                          if (input.value.trim()) {
                            setCrawlerSettings({
                              ...crawlerSettings,
                              keywords: [...crawlerSettings.keywords, input.value.trim()]
                            });
                            input.value = '';
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="card space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">수집 소스</h2>
                <p className="text-gray-600">활성화할 공모사업 수집 소스를 선택합니다.</p>
                
                <div className="space-y-3">
                  {crawlerSources.map((source, index) => (
                    <div key={source.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className={`w-2 h-2 rounded-full ${source.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <div>
                          <h3 className="font-medium text-gray-900">{source.name}</h3>
                          <p className="text-sm text-gray-500">
                            마지막 수집: {source.lastCrawl} {source.count > 0 && `(${source.count}건)`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          지금 수집
                        </button>
                        {renderToggle(source.enabled, (v) => {
                          const newSources = [...crawlerSources];
                          newSources[index].enabled = v;
                          setCrawlerSources(newSources);
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 데이터베이스 설정 */}
          {activeTab === 'database' && (
            <div className="card space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">데이터베이스 설정</h2>
              <p className="text-gray-600">데이터베이스 연결 및 백업을 관리합니다.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Database className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-medium text-gray-900">SQLite / PostgreSQL</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">상태</span>
                      <span className="text-green-600 font-medium">연결됨</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">호스트</span>
                      <span className="text-gray-900">localhost</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">데이터베이스</span>
                      <span className="text-gray-900">proposalcraft.db</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <RefreshCw className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium text-gray-900">백업</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">마지막 백업</span>
                      <span className="text-gray-900">2026-01-08 09:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">자동 백업</span>
                      <span className="text-green-600 font-medium">활성화</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">백업 주기</span>
                      <span className="text-gray-900">매일</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  수동 백업
                </button>
                <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                  백업 복원
                </button>
                <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors">
                  캐시 초기화
                </button>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-900">주의</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      데이터베이스 초기화 또는 캐시 삭제 시 일부 데이터가 손실될 수 있습니다. 
                      중요한 작업 전에 반드시 백업을 수행하세요.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              {/* 현재 구독 상태 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">현재 구독</h3>
                <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div>
                    <p className="text-lg font-bold text-emerald-800">무료 플랜</p>
                    <p className="text-sm text-emerald-600">기본 기능을 이용할 수 있습니다</p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded-full">활성</span>
                </div>
              </div>

              {/* 요금제 비교 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">요금제</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: 'Basic', price: '₩29,000/월', features: ['문서 50건/월', '맥락표 20건/월', 'AI 요청 200회/월', '5GB 저장공간'] },
                    { name: 'Pro', price: '₩79,000/월', features: ['문서 무제한', '맥락표 100건/월', 'AI 요청 1,000회/월', '50GB 저장공간', 'HWP 생성', '실시간 협업'], popular: true },
                    { name: 'Enterprise', price: '문의', features: ['모든 기능 무제한', '전용 지원', 'API 접근', '커스텀 템플릿', 'SLA 보장'] },
                  ].map((plan) => (
                    <div key={plan.name} className={`p-5 rounded-lg border-2 ${plan.popular ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                      {plan.popular && <span className="text-xs font-bold text-emerald-600 uppercase">인기</span>}
                      <h4 className="text-xl font-bold text-gray-900 mt-1">{plan.name}</h4>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{plan.price}</p>
                      <ul className="mt-4 space-y-2">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-center text-sm text-gray-600">
                            <svg className="h-4 w-4 text-emerald-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            {f}
                          </li>
                        ))}
                      </ul>
                      <button className={`mt-4 w-full py-2 rounded-lg text-sm font-medium ${plan.popular ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {plan.price === '문의' ? '영업팀 문의' : '업그레이드'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 크레딧 잔액 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI 크레딧</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-gray-900">0 <span className="text-sm font-normal text-gray-500">크레딧</span></p>
                    <p className="text-sm text-gray-500 mt-1">무료 플랜: 50 크레딧/월 제공</p>
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    크레딧 충전
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
